// PasscodeGate — lock screen shown when an album is passcode-protected and
// the current visitor doesn't have a valid cached token.
// On success, caches the token in localStorage (via setAlbumPassToken) so
// subsequent visits from this device skip the gate.
import React, { useState } from "react";
import { toast } from "sonner";
import { Lock, Eye, EyeOff } from "lucide-react";
import PageShell from "./PageShell";
import MeyecamLogo from "./MeyecamLogo";
import { Albums, setAlbumPassToken } from "../lib/api";
import { getIconConfig } from "../lib/eventIcons";

export default function PasscodeGate({ album, onUnlocked }) {
  const [passcode, setPasscode] = useState("");
  const [showPass, setShowPass] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const cfg = getIconConfig(album?.icon || "other");
  const Icon = cfg.Icon;

  const submit = async (e) => {
    e.preventDefault();
    const p = passcode.trim();
    if (!p) return;
    setSubmitting(true);
    setError("");
    try {
      const res = await Albums.verifyPasscode(album.slug, p);
      setAlbumPassToken(album.slug, res.token);
      toast.success("Unlocked");
      onUnlocked?.();
    } catch (err) {
      const status = err?.response?.status;
      const msg = err?.response?.data?.detail || (status === 401 ? "Incorrect passcode — try again" : "Couldn't verify — check your connection");
      setError(msg);
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <PageShell>
      <div className="flex flex-col items-center" style={{ paddingTop: 24 }}>
        <MeyecamLogo size={140} />
      </div>

      <form
        onSubmit={submit}
        className="mx-auto glass-card mt-6 p-6"
        style={{ maxWidth: 420 }}
        data-testid="passcode-gate-form"
      >
        <div className="flex items-center gap-3 mb-4">
          <div
            className="w-11 h-11 rounded-2xl flex items-center justify-center"
            style={{ background: `${cfg.color}22`, border: `1px solid ${cfg.color}66` }}
          >
            <Icon size={22} color={cfg.color} />
          </div>
          <div className="min-w-0">
            <div className="text-lg font-semibold truncate">{album.name}</div>
            <div className="text-xs flex items-center gap-1" style={{ color: "rgba(255,255,255,0.55)" }}>
              <Lock size={11} />
              <span>Private album · hosted by {album.host_name}</span>
            </div>
          </div>
        </div>

        <label className="text-xs font-semibold tracking-wider mb-2 block" style={{ color: "rgba(255,255,255,0.65)" }}>
          Enter passcode to view & upload
        </label>
        <div className="relative">
          <input
            className="meye-input pr-11"
            type={showPass ? "text" : "password"}
            value={passcode}
            onChange={(e) => { setPasscode(e.target.value); setError(""); }}
            placeholder="Ask the host for the code"
            autoFocus
            autoComplete="off"
            data-testid="passcode-input"
          />
          <button
            type="button"
            onClick={() => setShowPass((v) => !v)}
            className="absolute right-3 top-1/2 -translate-y-1/2 opacity-60 hover:opacity-100"
            aria-label={showPass ? "Hide passcode" : "Show passcode"}
            data-testid="passcode-toggle-visibility"
          >
            {showPass ? <EyeOff size={16} /> : <Eye size={16} />}
          </button>
        </div>

        {error && (
          <div
            role="alert"
            className="text-sm mt-3 rounded-xl px-3 py-2"
            style={{ background: "rgba(255,107,107,0.10)", border: "1px solid rgba(255,107,107,0.4)", color: "#FF9B9B" }}
            data-testid="passcode-gate-error"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !passcode.trim()}
          className="cta-pill w-full mt-4"
          data-testid="passcode-submit"
        >
          {submitting ? <div className="meye-spinner" /> : "Unlock album"}
        </button>

        <div className="text-xs mt-4 text-center" style={{ color: "rgba(255,255,255,0.45)" }}>
          The passcode is set by the host. You&rsquo;ll only need to enter it once on this device.
        </div>
      </form>
    </PageShell>
  );
}
